import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import TrackGroupedSchedule from "../components/TrackGroupedSchedule";
import ReplaysPanel from "../components/ReplaysPanel";
import CertificatePanel from "../components/CertificatePanel";
import PhotoGalleryPanel from "../components/PhotoGalleryPanel";
import BillingCard from "../components/BillingCard";
import ChangePasswordCard from "../components/ChangePasswordCard";
import DiscoveryRails from "../components/DiscoveryRails";
import MyPlaylistPanel from "../components/MyPlaylistPanel";
import useWatchProgress from "../hooks/useWatchProgress";
import useBookmarks from "../hooks/useBookmarks";
import {
  Calendar, User, LogOut, Lock, ExternalLink, Clock,
  Sparkles, ShieldCheck, History, CheckCircle2, Receipt, Search, X, Play, Bookmark, Camera
} from "lucide-react";

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [streamError, setStreamError] = useState(null);
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [scheduleData, setScheduleData] = useState([]); // for Continue watching
  const [playingTalk, setPlayingTalk] = useState(null); // shared modal state for rails/playlist
  const watchProgress = useWatchProgress();
  const bookmarks = useBookmarks();

  const isBookmarked = useCallback(
    (sid) => bookmarks.items.some((b) => b.session_id === sid),
    [bookmarks.items]
  );
  const toggleBookmark = useCallback((sess) => bookmarks.toggle(sess), [bookmarks]);

  // When a rail card or playlist card is clicked, open the same TalkModal
  // used by the schedule and auto-mark in_progress.
  const openTalk = useCallback((sess) => {
    if (!sess?.video_url) return;
    setPlayingTalk(sess);
    watchProgress.mark(sess.session_id, "in_progress");
  }, [watchProgress]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/conference/stream");
        setStream(r.data);
      } catch (e) {
        setStreamError(e?.response?.status === 402 ? "no_ticket" : "error");
      }
      try {
        const sp = await api.get("/conference/speakers");
        setSpeakers(sp.data.speakers.slice(0, 4));
      } catch {}
      try {
        const sc = await api.get("/conference/schedule");
        setScheduleData(sc.data?.schedule || []);
      } catch {}
    })();
  }, []);

  // Build a flat list of in-progress (not yet watched) sessions for the
  // "Continue watching" rail. Most recently opened first.
  const continueWatching = useMemo(() => {
    const flat = [];
    for (const day of scheduleData) {
      for (const s of (day.sessions || [])) {
        const wp = watchProgress.items[s.session_id];
        if (wp?.status === "in_progress" && s.video_url) {
          flat.push({ session: s, updated_at: wp.updated_at });
        }
      }
    }
    flat.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    return flat.slice(0, 6);
  }, [scheduleData, watchProgress.items]);

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex" data-testid="portal-page">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-72 border-r border-black/5 bg-white p-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-3" data-testid="portal-logo">
          <div className="leading-none">
            <div className="flex items-baseline gap-0.5">
              <span className="font-heading text-2xl font-black text-[#212121] tracking-tight">DFTB</span>
              <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-1">Virtual portal</div>
          </div>
        </Link>

        <nav className="mt-10 space-y-1 text-sm">
          {[
            { icon: History, label: "Watch back", to: "#replays", active: true },
            { icon: Sparkles, label: "Discover", to: "#discover" },
            { icon: Bookmark, label: "My playlist", to: "#my-playlist" },
            { icon: Calendar, label: "Schedule", to: "#schedule" },
            ...(user?.ticket_source === "face_to_face" || user?.role === "admin"
              ? [{ icon: Camera, label: "Photos", to: "#photos" }]
              : []),
            { icon: User, label: "Profile", to: "#profile" },
            { icon: Receipt, label: "Billing", to: "#billing" },
          ].map((item, i) => {
            const Icon = item.icon;
            const cls = `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${item.active ? "bg-[#28BBE3]/10 text-[#28BBE3]" : "text-slate-600 hover:bg-slate-50"}`;
            return item.external ? (
              <a key={i} href={item.to} target="_blank" rel="noreferrer" className={cls} data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={17} strokeWidth={1.6} /><span className="flex-1">{item.label}</span>
                <ExternalLink size={13} />
              </a>
            ) : (
              <a key={i} href={item.to} className={cls} data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon size={17} strokeWidth={1.6} /><span>{item.label}</span>
              </a>
            );
          })}
          {user?.role === "admin" && (
            <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="portal-nav-admin">
              <ShieldCheck size={17} strokeWidth={1.6} /><span>Admin dashboard</span>
            </Link>
          )}
        </nav>

        <div className="mt-auto pt-6 border-t border-black/5">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
            ) : (
              <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button variant="ghost" className="mt-4 w-full justify-start text-slate-600 hover:text-slate-900"
            onClick={async () => { await logout(); navigate("/"); }} data-testid="portal-logout-btn">
            <LogOut size={16} className="mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0">
        {/* MOBILE TOP BAR (sidebar is hidden under lg breakpoint) */}
        <div className="lg:hidden border-b border-black/5 bg-white px-6 py-3 flex items-center justify-between gap-3" data-testid="portal-mobile-bar">
          <Link to="/" className="flex items-center" data-testid="portal-mobile-logo">
            <div className="leading-none">
              <div className="flex items-baseline gap-0.5">
                <span className="font-heading text-xl font-black text-[#212121] tracking-tight">DFTB</span>
                <span className="font-heading text-xl font-black text-[#28BBE3] tracking-tight">26</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-slate-500 font-bold mt-0.5">Virtual portal</div>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-colors"
                data-testid="portal-mobile-admin-link"
              >
                <ShieldCheck size={13} strokeWidth={2} /> Admin
              </Link>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full px-3 text-slate-600 hover:text-slate-900"
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="portal-mobile-logout-btn"
            >
              <LogOut size={15} />
            </Button>
          </div>
        </div>

        <div className="border-b border-black/5 bg-white/70 backdrop-blur-md sticky top-0 z-10">
          <div className="px-6 sm:px-10 py-5 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Welcome back</div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                Hi, {user?.name?.split(" ")[0] || "there"}.
              </h1>
            </div>
            {user?.has_ticket || user?.role === "admin" ? (
              <Badge className="rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0 px-3 py-1" data-testid="portal-ticket-badge">
                <Sparkles size={12} className="mr-1.5" /> Virtual ticket active
              </Badge>
            ) : (
              <Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100 border-0 px-3 py-1" data-testid="portal-ticket-badge">
                No ticket yet
              </Badge>
            )}
          </div>
        </div>

        <div className="p-6 sm:p-10 space-y-10">
          {/* POST-CONFERENCE BANNER (replaces the old live stream player) */}
          <section id="stream" data-testid="portal-postconf-banner">
            {streamError === "no_ticket" ? (
              <div className="rounded-3xl bg-white ring-1 ring-black/5 p-10 text-center" data-testid="portal-no-ticket">
                <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                  <Lock className="text-amber-700" size={22} strokeWidth={1.6} />
                </div>
                <h2 className="mt-6 font-heading text-2xl text-slate-900 font-medium tracking-tight">
                  You don&apos;t have a ticket yet.
                </h2>
                <p className="mt-2 text-slate-600 max-w-md mx-auto">
                  Grab your DFTB26 virtual ticket to unlock all 60+ talks on demand for 12 months.
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <Button onClick={() => navigate("/#tickets")}
                    className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white px-6 py-6 font-semibold"
                    data-testid="portal-buy-full-btn">
                    Full £397
                  </Button>
                  <Button onClick={() => navigate("/#tickets")}
                    variant="outline"
                    className="rounded-full border-slate-300 text-slate-800 px-6 py-6 font-semibold hover:bg-slate-50"
                    data-testid="portal-buy-concession-btn">
                    Concession £247
                  </Button>
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Work in a low/middle-income country?{" "}
                  <button
                    onClick={() => navigate("/#tickets")}
                    className="underline hover:text-slate-700"
                    data-testid="portal-lmic-link"
                  >
                    Apply for free access
                  </button>
                </p>
              </div>
            ) : (
              <div className="rounded-3xl bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white p-6 sm:p-8 ring-1 ring-black/5" data-testid="portal-postconf-card">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="h-12 w-12 rounded-2xl bg-[#28BBE3]/20 ring-1 ring-[#28BBE3]/30 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="text-[#28BBE3]" size={22} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">DFTB26 — Back to Basics</div>
                    <h2 className="mt-1 font-heading text-xl sm:text-2xl font-medium tracking-tight">
                      The live conference has ended.
                    </h2>
                    <p className="mt-2 text-sm sm:text-base text-white/70 leading-relaxed max-w-2xl">
                      Thanks for being part of DFTB26. Full-day recordings are available now in <a href="#replays" className="text-[#28BBE3] hover:underline font-medium">Watch back</a>, with individual talks added to the <a href="#schedule" className="text-[#28BBE3] hover:underline font-medium">schedule</a> as soon as they&apos;re cut.
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs text-white/60">
                      <Clock size={13} /> Replay access until <strong className="text-white">22 June 2027</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* WATCH BACK — replays of completed live streams (only renders when published replays exist) */}
          <ReplaysPanel />

          {/* DISCOVERY RAILS — Editor's picks (curated) + Most watched (auto). Self-hides if both are empty. */}
          <DiscoveryRails onOpenTalk={openTalk} />

          {/* MY PLAYLIST — user's saved talks. Self-hides when empty. */}
          <MyPlaylistPanel
            items={bookmarks.items}
            onOpenTalk={openTalk}
            onRemove={(sid) => bookmarks.toggle(sid)}
            busyId={bookmarks.busyId}
          />

          {/* CONTINUE WATCHING — talks marked in_progress, most recent first. */}
          {continueWatching.length > 0 && (
            <section
              id="continue-watching"
              className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8"
              data-testid="continue-watching-panel"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Pick up where you left off</div>
                  <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">Continue watching</h3>
                </div>
                <a href="#schedule" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                  Browse all talks <ExternalLink size={11} />
                </a>
              </div>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="continue-watching-list">
                {continueWatching.map(({ session }) => (
                  <a
                    key={session.session_id}
                    href="#schedule"
                    onClick={() => {
                      // Scroll triggers schedule section; user can click "Watch talk" again.
                    }}
                    className="group rounded-2xl ring-1 ring-black/5 p-4 hover:ring-[#28BBE3]/40 hover:bg-[#28BBE3]/[0.03] transition-colors"
                    data-testid={`continue-card-${session.session_id}`}
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-amber-700 font-semibold">
                      <Play size={10} strokeWidth={2.6} /> In progress
                    </div>
                    <div className="mt-1.5 text-sm font-medium text-slate-900 line-clamp-2 leading-snug">
                      {session.title}
                    </div>
                    {session.speaker && (
                      <div className="text-xs text-slate-500 mt-1 truncate">{session.speaker}</div>
                    )}
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* CONFERENCE PHOTO GALLERY — visible for in-person attendees + admins.
              Component self-hides if the user isn't entitled or no URL is set. */}
          <PhotoGalleryPanel />

          {/* ATTENDANCE CERTIFICATE — available to everyone with a ticket
              (F2F attendees get a variant with "Attended in person" venue line). */}
          <CertificatePanel />

          {/* SCHEDULE + SPEAKERS GRID */}
          <section id="schedule" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Watch on demand</div>
                  <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">All talks</h3>
                </div>
                <Calendar className="text-slate-400" size={20} strokeWidth={1.5} />
              </div>

              {/* SEARCH — filters by title + speaker + room name. */}
              <div className="mt-4 relative">
                <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" strokeWidth={1.8} />
                <Input
                  type="text"
                  value={scheduleSearch}
                  onChange={(e) => setScheduleSearch(e.target.value)}
                  placeholder="Search talks by title, speaker or room…"
                  className="pl-10 pr-9 h-11 rounded-full bg-slate-50 border-slate-200 focus-visible:ring-[#28BBE3]/30"
                  data-testid="schedule-search-input"
                />
                {scheduleSearch && (
                  <button
                    type="button"
                    onClick={() => setScheduleSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 flex items-center justify-center"
                    data-testid="schedule-search-clear"
                    aria-label="Clear search"
                  >
                    <X size={12} strokeWidth={2.4} />
                  </button>
                )}
              </div>

              <div className="mt-5">
                <TrackGroupedSchedule
                  variant="light"
                  showAsk={false}
                  rooms={stream?.rooms || []}
                  searchQuery={scheduleSearch}
                  watchProgress={watchProgress.items}
                  onMarkWatched={watchProgress.mark}
                  isBookmarked={isBookmarked}
                  onToggleBookmark={toggleBookmark}
                  externalPlayingTalk={playingTalk}
                  onTalkModalChange={setPlayingTalk}
                />
              </div>
            </div>

            <div className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Featured speakers</div>
              <h3 className="mt-1 font-heading text-xl text-slate-900 font-medium tracking-tight">The faculty</h3>
              <div className="mt-5 space-y-3">
                {speakers.map((s) => (
                  <div key={s.id} className="flex items-center gap-3" data-testid={`portal-speaker-${s.id}`}>
                    <img src={s.picture} alt={s.name} className="h-12 w-12 rounded-full object-cover ring-1 ring-black/5" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.topic}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* PROFILE */}
          <section id="profile" className="rounded-3xl bg-white ring-1 ring-black/5 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover ring-1 ring-black/5" />
                ) : (
                  <div className="h-14 w-14 rounded-full bg-slate-900 text-white flex items-center justify-center text-base font-semibold">{initials}</div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Profile</div>
                  <div className="font-heading text-lg text-slate-900 font-medium">{user?.name}</div>
                  <div className="text-sm text-slate-500">{user?.email}</div>
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Signed in via <span className="font-medium text-slate-700 capitalize">{user?.auth_provider}</span>
              </div>
            </div>

            {user?.auth_provider === "password" && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <ChangePasswordCard />
              </div>
            )}
          </section>

          {/* BILLING — paid transactions + resend-receipt buttons. Self-hides for users with 0 paid txs. */}
          <BillingCard />
        </div>
      </main>
    </div>
  );
}
